import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  SprayCalculatorService,
  sprayCalculatorKeys,
} from '@client/services/sprayCalculatorService';

/**
 * Hook to fetch liquid volume units (for tank volume)
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with liquid volume units
 */
export const useLiquidVolumeUnits = (options = {}) => {
  return useQuery({
    queryKey: sprayCalculatorKeys.liquidVolumeUnits(),
    queryFn: () => SprayCalculatorService.getLiquidVolumeUnits(),
    staleTime: 60 * 60 * 1000, // 1 hour - units don't change often
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
    ...options,
  });
};

/**
 * Hook to fetch area units
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with area units
 */
export const useAreas = (options = {}) => {
  return useQuery({
    queryKey: sprayCalculatorKeys.areas(),
    queryFn: () => SprayCalculatorService.getAreas(),
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
    ...options,
  });
};

/**
 * Hook to fetch measurement units (for row spacing, band width)
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with measurement units
 */
export const useMeasurementUnits = (options = {}) => {
  return useQuery({
    queryKey: sprayCalculatorKeys.measurementUnits(),
    queryFn: () => SprayCalculatorService.getMeasurementUnits(),
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
    ...options,
  });
};

/**
 * Hook to fetch water volume per area units
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with water volume per area units
 */
export const useWaterVolumePerAreas = (options = {}) => {
  return useQuery({
    queryKey: sprayCalculatorKeys.waterVolumePerAreas(),
    queryFn: () => SprayCalculatorService.getWaterVolumePerAreas(),
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 2 * 60 * 60 * 1000, // 2 hours
    ...options,
  });
};

/**
 * Hook to fetch product dose units with optional filters
 * @param {Object} [params={}] - Query parameters (dose_type, unit_form)
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with product dose units
 */
export const useProductDoses = (params = {}, options = {}) => {
  return useQuery({
    queryKey: sprayCalculatorKeys.productDoses(params),
    queryFn: () => SprayCalculatorService.getProductDoses(params),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    enabled: options.enabled !== false,
    ...options,
  });
};

/**
 * Hook to search commercial products
 * @param {Object} [params={}] - Search parameters
 * @param {string} [params.search] - Search term
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with commercial products
 */
export const useSearchProducts = (params = {}, options = {}) => {
  return useQuery({
    queryKey: sprayCalculatorKeys.productsSearch(params),
    queryFn: () => SprayCalculatorService.searchCommercialProducts(params),
    enabled: !!params.search,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Hook to calculate spray volumes (mutation)
 * @param {Object} [options={}] - Additional React Query mutation options
 * @returns {Object} React Query mutation result
 * @example
 * const { mutate: calculate, isPending } = useCalculateSpray({
 *   onSuccess: (data) => console.log('Results:', data),
 *   onError: (error) => console.error('Error:', error),
 * });
 * calculate(payload);
 */
export const useCalculateSpray = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => SprayCalculatorService.calculate(payload),
    onSuccess: (data, variables) => {
      // Cache the calculation result
      queryClient.setQueryData(sprayCalculatorKeys.calculation(variables), data);
    },
    ...options,
  });
};
